import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Anmeldung des Servicearbeiters.
 *
 * Zwei Dinge, die man leicht falsch macht und die hier bewusst so stehen:
 *
 * **Nur im Produktionsstand.** Im Entwicklungsbetrieb gibt es kein /sw.js, und
 * ein einmal angemeldeter Arbeiter wuerde dort Dateien ausliefern, die gerade
 * geaendert werden. Das kostet Stunden Fehlersuche an einem Fehler, den es
 * nicht gibt.
 *
 * **Kein stiller Austausch.** Eine neue Fassung wartet, bis der Nutzer sie
 * annimmt. Wer mitten in einer Checkliste steht, soll nicht dadurch
 * unterbrochen werden, dass jemand im Buero eine Auslieferung angestossen hat.
 */

let wartend: ServiceWorker | null = null;
/*
 * Hat der Nutzer den Wechsel selbst angestossen? Dann wird immer neu geladen.
 * Ohne diese Merkung blieb der Balken nach dem Klick stehen, wenn der Arbeiter
 * in derselben Sitzung erst installiert worden war: die Seite war beim Laden
 * noch nicht von einem Arbeiter gesteuert, und die Bedingung dafuer traf
 * deshalb nicht zu. Sichtbar wurde das erst in der Messung mit einem echten
 * Browser.
 */
let uebernahmeAngefordert = false;

export function serviceArbeiterAnmelden(neueFassungGemeldet: () => void): void {
  if (!import.meta.env.PROD) return;
  if (!("serviceWorker" in navigator)) return;

  /*
   * Nicht schlicht auf "load" warten.
   *
   * Diese Funktion laeuft aus einem Effekt der Anwendung, und der kann erst
   * nach dem Ladeereignis an die Reihe kommen, etwa wenn alle Dateien schon im
   * Zwischenspeicher liegen. Dann feuert "load" nie wieder und der
   * Servicearbeiter wuerde sich nie anmelden. Genau das war beim ersten
   * Versuch der Fall, sichtbar erst in einem echten Browser: null Anmeldungen,
   * kein Fehler, keine Meldung.
   *
   * Deshalb: ist das Laden durch, sofort anmelden, sonst beim Ladeereignis.
   * Verzoegert wird ueberhaupt nur, um dem ersten Aufbau der Seite nicht die
   * Bandbreite wegzunehmen.
   */
  const beginnen = () => {
    void navigator.serviceWorker
      /*
       * updateViaCache "none": der Arbeiter selbst wird nie aus dem
       * Zwischenspeicher des Browsers genommen, sondern immer beim Server
       * erfragt. Sonst kann eine Auslieferung tagelang unsichtbar bleiben,
       * weil das Geraet den alten Arbeiter fuer aktuell haelt.
       */
      .register("/sw.js", { updateViaCache: "none" })
      .then((anmeldung) => {
        // Schon beim Start kann eine Fassung fertig danebenliegen, etwa weil
        // der Nutzer beim letzten Mal nicht neu geladen hat.
        if (anmeldung.waiting) {
          wartend = anmeldung.waiting;
          neueFassungGemeldet();
        }

        anmeldung.addEventListener("updatefound", () => {
          const neu = anmeldung.installing;
          if (!neu) return;
          neu.addEventListener("statechange", () => {
            // installed mit vorhandenem Controller heisst: es gab schon eine
            // Fassung, diese hier ist die neue. Ohne Controller ist es die
            // erste Installation ueberhaupt, und die meldet man nicht.
            if (neu.state === "installed" && navigator.serviceWorker.controller) {
              wartend = neu;
              neueFassungGemeldet();
            }
          });
        });

        // Einmal beim Start nachsehen. Ein Telefon, das wochenlang offen
        // liegt, fragt sonst von sich aus nur selten nach.
        void anmeldung.update().catch(() => {});
      })
      .catch(() => {
        /* Ohne Servicearbeiter laeuft alles wie vorher, nur eben nicht ohne Netz. */
      });

    /*
     * Neu laden nur nach einem Wechsel, nicht nach der ersten Installation.
     *
     * Beim allerersten Besuch uebernimmt der frisch installierte Arbeiter die
     * schon offene Seite, und das loest ebenfalls controllerchange aus. Wer
     * hier ohne Unterscheidung neu laedt, laedt jedem Nutzer beim ersten
     * Aufruf die Seite unter den Haenden neu. In der Messung mit einem echten
     * Browser war genau das zu sehen.
     */
    const hatteController = !!navigator.serviceWorker.controller;
    let neugeladen = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (neugeladen) return;
      if (!uebernahmeAngefordert && !hatteController) return;
      neugeladen = true;
      window.location.reload();
    });
  };

  if (document.readyState === "complete") beginnen();
  else window.addEventListener("load", beginnen, { once: true });
}

/** Der Nutzer nimmt die neue Fassung an. Die Seite laedt danach von selbst neu. */
export function neueFassungUebernehmen(): void {
  uebernahmeAngefordert = true;
  wartend?.postMessage({ typ: "uebernehmen" });
}

/**
 * Der Balken, der eine neue Fassung anbietet.
 *
 * Er haengt bewusst ueber der ganzen Anwendung und nicht in ihr: er soll auch
 * vor der Anmeldung erscheinen. Wer sein Passwort nicht mehr weiss und auf dem
 * Anmeldebild steht, sitzt sonst auf einer alten Fassung fest, ohne dass ihm
 * jemand sagt, dass es eine neue gibt.
 */
export function NeueFassungBalken() {
  const { t } = useTranslation();
  const [bereit, setBereit] = useState(false);

  useEffect(() => {
    serviceArbeiterAnmelden(() => setBereit(true));
  }, []);

  if (!bereit) return null;
  return (
    <div className="flex flex-wrap items-center justify-center gap-3 bg-tinte px-4 py-2 text-sm text-grund">
      <span>{t("app.neueFassung")}</span>
      <button className="font-semibold text-marke underline" onClick={neueFassungUebernehmen}>
        {t("app.neueFassungLaden")}
      </button>
    </div>
  );
}
